import { useGetDeployment, useTriggerTenantMigration } from '@sunstone/api-client/hooks';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@sunstone/ui';
import { ChevronDown, Loader2, RefreshCw, Server } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { PageHeader } from '@/components/ui/PageHeader';
import { useAuthToken } from '@/features/auth';
import { useAuthorization } from '@/features/auth/useAuthorization';
import { DeploymentDetail } from '@/features/deployments/DeploymentDetail';
import { DeploymentList } from '@/features/deployments/DeploymentList';
import { routes } from '@/lib/routes';
import { deferDialogOpen } from '@/lib/utils';

export function DeploymentsPage() {
  const { tenantId, deploymentId } = useParams<{ tenantId: string; deploymentId: string }>();
  const navigate = useNavigate();
  const { token } = useAuthToken();
  const { data: deploymentData } = useGetDeployment(deploymentId ?? '', token);
  const deployment = deploymentData?.deployment;
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { canManageAccess, hasPermission } = useAuthorization();
  const importTenants = useTriggerTenantMigration(token);

  // ── Detail view: /tenants/:tenantId/deployments/:deploymentId ─────────────
  if (tenantId && deploymentId) {
    return (
      <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <PageHeader
          title={deployment?.displayName ?? '…'}
          description={
            <>
              <Server className="h-3.5 w-3.5" />
              Deployment
            </>
          }
          action={
            <div className="flex items-center gap-2">
              {canManageAccess && deployment?.slug.startsWith('baic-') && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!deployment || importTenants.isPending}
                  onClick={() =>
                    importTenants.mutate(deploymentId ?? '', {
                      onSuccess: () => toast.success('Tenant import started'),
                      onError: (e) => toast.error(e.message),
                    })
                  }
                >
                  {importTenants.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                  Import Tenants
                </Button>
              )}
              {hasPermission('delete:deployment') && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" disabled={!deployment}>
                      More Actions
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem disabled>Maintenance Mode</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      variant="destructive"
                      onClick={() => deferDialogOpen(() => setDeleteOpen(true))}
                    >
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {hasPermission('create:workspace') && (
                <Button
                  size="sm"
                  disabled={!deployment}
                  onClick={() =>
                    navigate(`/tenants/${tenantId}/deployments/${deploymentId}/workspaces/new`)
                  }
                >
                  Add Workspace
                </Button>
              )}
            </div>
          }
        />
        <DeploymentDetail
          deploymentId={deploymentId}
          deleteOpen={deleteOpen}
          onDeleteOpenChange={setDeleteOpen}
        />
      </div>
    );
  }

  // ── Global list view: /deployments ────────────────────────────────────────
  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <PageHeader
        title="Deployments"
        description="Track all cloud and on-premises deployments across customers."
        action={
          hasPermission('create:deployment') && (
            <Button size="sm" onClick={() => navigate(routes.newDeploymentGlobal())}>
              New Deployment
            </Button>
          )
        }
      />
      <DeploymentList urlParams />
    </div>
  );
}
